import { useEffect } from 'react'
import Button from '@mui/material/Button'
import AddIcon from '@mui/icons-material/Add'
import RemoveIcon from '@mui/icons-material/Remove'
import Link from 'src/components/Link'
import AiGeneratedBadge from 'src/components/AiGeneratedBadge'
import QuantityDiscountInfo from 'src/components/QuantityDiscountInfo'
import StockDiscountPriceBreakdown, { hasStockDiscountPriceBreakdown } from 'src/components/StockDiscountPriceBreakdown'
import getImageUrl from 'src/pages/Product/functions/imageUrl'
import getProductUrl from 'src/functions/productUrl'
import formatEuro from 'src/functions/formatEuro'
import renderDeliverytime from 'src/functions/renderDeliverytime'
import { isQuantityDiscountAvailable } from 'src/shared/discountShop'
import type { tItem } from 'src/shared/frontendTypes/warenkorb'
import { getCartLinePresentation } from './cartPresentation'
import styles from './ModernCart.module.css'

type Props = {
  item: tItem
  changeQty: (key: string, qty: number) => void
  deleteItem: (key: string) => void
}

export default function CartItem({ item, changeQty, deleteItem }: Props) {
  const { id, key, name, image, path, attributes, attributesText, price, qty, onlyStock, stock = 0, deliverytime = 0, stockDeliverytime } = item
  const inquiryOnly = item.inquireOnly || item.purchaseMode === 'inquiry'
  const maxQty = onlyStock ? Math.max(0, stock) : Infinity
  // Keep stock correction out of render; the existing cart also caps discontinued items.
  useEffect(() => {
    if (!inquiryOnly && qty > maxQty) changeQty(key, maxQty)
  }, [key, qty, maxQty, inquiryOnly, changeQty])
  const url = getProductUrl(id, path, attributes ? Object.values(attributes) : [])
  const imageUrl = image && (getImageUrl(image, id, 'sm') || getImageUrl(image, id, 'xs'))
  const { total, regularTotal, saving, percent } = getCartLinePresentation(item)
  const splitPrice = typeof price !== 'number' && price.type !== 'invisible' && hasStockDiscountPriceBreakdown(price, qty)
  const quantityDiscount = typeof price !== 'number' && price.showPercent !== false
    && isQuantityDiscountAvailable(price.quantityDiscount, { onlyStock, stock, dateEnd: price.dateEnd })

  return <article className={styles.item} aria-label={name} data-cart-item={key}>
    <Link href={url} className={styles.imageLink} aria-label={`${name} ansehen`}>
      {imageUrl ? <img src={imageUrl} width={120} height={144} alt={name} /> : <span className={styles.imagePlaceholder}>Kein Bild</span>}
      <AiGeneratedBadge aiGenerated={image?.aiGenerated} />
    </Link>
    <div className={styles.itemDetails}>
      {item.brand?.titel && <span className={styles.brand}>{item.brand.titel}</span>}
      <h2 className={styles.itemTitle}><Link href={url}>{name}</Link></h2>
      {attributesText && <p className={styles.attributes}>
        {attributesText.map(attribute => attribute.option).join(' · ')}
      </p>}
      <div className={styles.priceLine}>
        {saving > 0 && <del>{formatEuro(regularTotal)}</del>}
        {percent > 0 && <span className={styles.discount}>−{percent} %</span>}
        <strong>{formatEuro(total)}</strong>
      </div>
      {saving > 0 && <p className={styles.saving}>Du sparst {formatEuro(saving)}{qty > 1 ? ' bei diesen Artikeln' : ' bei diesem Artikel'}.</p>}
      {qty > 1 && !splitPrice && <p className={styles.unitPrice}>{formatEuro(total / qty)} pro Stück · {qty} Stück</p>}
      <div className={styles.delivery}>
        {inquiryOnly ? <p>Nur auf Anfrage erhältlich</p> : splitPrice
          ? <StockDiscountPriceBreakdown price={price} qty={qty} deliverytime={deliverytime} stockDeliverytime={stockDeliverytime} showDeliverytime showBackorderNotice />
          : (stock > 0 || deliverytime > 0) && renderDeliverytime(deliverytime, qty, stock, stockDeliverytime)}
      </div>
      {quantityDiscount && typeof price !== 'number' && <QuantityDiscountInfo quantityDiscount={price.quantityDiscount} qty={qty} dateEnd={price.dateEnd} />}
      <div className={styles.itemActions}>
        <div className={styles.quantity} role='group' aria-label={`Menge für ${name}`}>
          <Button color='inherit' aria-label={`Weniger ${name}`} onClick={() => changeQty(key, qty - 1)} disabled={qty < 2 || inquiryOnly}><RemoveIcon fontSize='small' /></Button>
          <span aria-live='polite' aria-atomic='true'>{qty}</span>
          <Button color='inherit' aria-label={`Mehr ${name}`} onClick={() => changeQty(key, qty + 1)} disabled={qty >= maxQty || inquiryOnly}><AddIcon fontSize='small' /></Button>
        </div>
        <Button color='inherit' className={styles.remove} onClick={() => deleteItem(key)} aria-label={`${name} entfernen`}>Entfernen</Button>
      </div>
      {!inquiryOnly && onlyStock && qty >= maxQty && <p className={styles.stockNotice}>Nur noch {maxQty} Stück verfügbar.</p>}
    </div>
  </article>
}
